import { z } from 'zod/v4';

export const AuditParamsSchema = z.object({ id: z.string().min(1) });
export type AuditParams = z.infer<typeof AuditParamsSchema>;

/**
 * Full `files` row (+ the service-derived `previewUrl`) as returned inside
 * `auditFile`/`capFile` on the audit-detail wire — 24 columns selected by
 * `core/db/audits/get-file-by-id.ts` plus `previewUrl`. Keys are always
 * present (pg emits every selected column, NULL included); the whole object
 * is `null` when the audit has no file or the row no longer exists.
 *
 * `fileSize` is bigint (driver-parsed to number), `isMigrated` is integer.
 * `createdAt`/`updatedAt` are ISO strings (normalised in
 * `AuditsService.#buildFileDetail`).
 */
export const AuditFileDetailSchema = z.looseObject({
  id: z.string(),
  bucketName: z.string(),
  url: z.string().nullable(),
  name: z.string().nullable(),
  fileType: z.string().nullable(),
  fileSize: z.number().nullable(),
  contentType: z.string().nullable(),
  fileHash: z.string().nullable(),
  isMigrated: z.number().nullable(),
  createdAt: z.string().nullable(),
  createdByImpersonateId: z.string().nullable(),
  createdByUserId: z.string().nullable(),
  createdByCompanyId: z.string().nullable(),
  createdByApiKeyId: z.string().nullable(),
  createdByApiKeyLabel: z.string().nullable(),
  updatedAt: z.string().nullable(),
  updatedByImpersonateId: z.string().nullable(),
  updatedByImpersonateName: z.string().nullable(),
  updatedByUserId: z.string().nullable(),
  updatedByUserName: z.string().nullable(),
  updatedByCompanyId: z.string().nullable(),
  updatedByCompanyName: z.string().nullable(),
  updatedByApiKeyId: z.string().nullable(),
  updatedByApiKeyLabel: z.string().nullable(),
  previewUrl: z.string().nullable(),
});

export type AuditFileDetail = z.infer<typeof AuditFileDetailSchema>;

/**
 * One audit on the `GET /api/audits/:id` wire — the closed 49-key set:
 * the 43 columns selected by `core/db/audits/get-scoped-by-id.ts` plus the
 * keys `AuditsService` derives (`auditReportFileOriginalUrl`,
 * `correctiveActionPlanFileOriginalUrl`, `rights`, `privacyStatus`,
 * `auditFile`, `capFile`; the two `*FileUrl` columns are overwritten with
 * preview URLs). Every key is always present.
 *
 * Wire-format notes (do not "fix" — both frontends read this shape):
 * - `isPublic` is the raw `0`/`1` integer, NOT a boolean (unlike the list,
 *   whose transformer booleanizes it).
 * - Dates are ISO strings — normalised in `AuditsService.#buildAudit`; the
 *   serializer validates declared fields, so a raw `Date` here would 500.
 * - `rights` is always `[]`: the scoped query deliberately does not join
 *   access-control.
 * - `correctiveActionPlanFileFileType` keeps the legacy double-"File" alias.
 *
 * Deliberately NOT declared (never on this wire — the scoped query selects
 * none of them): `country`, `generatedBy`, `digitizationStatus`,
 * `requestStatus`, `digitizationPercentage`. `z.looseObject` keeps any
 * future extra column flowing; the strict flip is deferred until the whole
 * module is migrated.
 */
export const AuditDetailRowSchema = z.looseObject({
  id: z.string(),
  companyId: z.string().nullable(),
  companyName: z.string().nullable(),
  city: z.string().nullable(),
  name: z.string().nullable(),
  auditTypeId: z.string().nullable(),
  auditTypeName: z.string().nullable(),
  externalSource: z.string().nullable(),
  type: z.string().nullable(),
  auditedBy: z.string().nullable(),
  auditDate: z.string().nullable(),
  referenceNumber: z.string().nullable(),
  facilityScore: z.string().nullable(),
  nextPlannedAuditDate: z.string().nullable(),
  dataStatus: z.string().nullable(),
  auditReportFileId: z.string().nullable(),
  auditReportFileUrl: z.string().nullable(),
  auditReportFileOriginalUrl: z.string().nullable(),
  auditReportFileBucketName: z.string().nullable(),
  auditReportFileType: z.string().nullable(),
  auditReportFileContentType: z.string().nullable(),
  correctiveActionPlanFileId: z.string().nullable(),
  correctiveActionPlanFileUrl: z.string().nullable(),
  correctiveActionPlanFileOriginalUrl: z.string().nullable(),
  correctiveActionPlanFileBucketName: z.string().nullable(),
  correctiveActionPlanFileFileType: z.string().nullable(),
  correctiveActionPlanFileContentType: z.string().nullable(),
  uploaderCompanyId: z.string().nullable(),
  uploaderCompanyName: z.string().nullable(),
  createdAt: z.string().nullable(),
  createdByImpersonateId: z.string().nullable(),
  createdByUserId: z.string().nullable(),
  createdByCompanyId: z.string().nullable(),
  createdByApiKeyId: z.string().nullable(),
  createdByApiKeyLabel: z.string().nullable(),
  updatedAt: z.string().nullable(),
  updatedByImpersonateId: z.string().nullable(),
  updatedByImpersonateName: z.string().nullable(),
  updatedByUserId: z.string().nullable(),
  updatedByUserName: z.string().nullable(),
  updatedByCompanyId: z.string().nullable(),
  updatedByCompanyName: z.string().nullable(),
  updatedByApiKeyId: z.string().nullable(),
  updatedByApiKeyLabel: z.string().nullable(),
  isPublic: z.number().nullable(),
  rights: z.array(z.string()),
  privacyStatus: z.object({ status: z.enum(['public', 'private']) }).nullable(),
  auditFile: AuditFileDetailSchema.nullable(),
  capFile: AuditFileDetailSchema.nullable(),
});

export type AuditDetailRow = z.infer<typeof AuditDetailRowSchema>;
